import {
  Configure,
  InstantSearch,
} from 'react-instantsearch-dom'
import client from './algoliaClient';
import { AlgoliaIndexes, DomainObjects } from 'projectlink-common';
import { createContext, FC, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

const initialHitsPerPage = 8
const GLOBAL_FILTERS = ['status:published']

const searchConfigContext = createContext({
  selectedTopics: [],
  selectedType: null,
  setSelectedTopics: (p: any) => null,
  setSelectedType: (p: any) => null,
  paginate: (currentHits: number) => null,
})

const useSearchConfigContext = () => useContext(searchConfigContext)

const resultsState = (hits, query = '') => ({
  metadata: [],
  state: {
    index: 'main',
    query: '',
  },
  rawResults: [
    {
      hits,
      nbHits: 0,
      page: 0,
      nbPages: 0,
      hitsPerPage: 8,
      exhaustiveNbHits: true,
      query,
      queryAfterRemoval: '',
      params: '',
      index: 'main',
      processingTimeMS: 2,
    },
  ],
})

const filterString = (filters: string[]) => {
  return filters.filter(x => x).join(' AND ')
}

const SearchableFeedProvider: FC<{
  initialHits?: any[],
  globalFilters?: string[],
  initialType?: DomainObjects,
  indexName: AlgoliaIndexes,
}> = ({
  initialHits = [],
  globalFilters = [],
  initialType,
  indexName,
  children,
}) => {
    const [hitsPerPage, setHitsPerPage] = useState(initialHitsPerPage)
    const router = useRouter()
    const initialized = useRef(false)
    const initialTopics = (router?.query?.topics as string || '').split(',').filter(x => x)
    const _initialType = (router?.query?.type as string || initialType) || null
    const [selectedTopics, setSelectedTopics] = useState(initialTopics)
    const [selectedType, setSelectedType] = useState(_initialType)
    const aggregatedGlobalFilters = [...GLOBAL_FILTERS, ...globalFilters]

    const selectedTopicsFilter = selectedTopics
      .map((topic, index) => `topics:"${topic}${index === selectedTopics.length - 1 ? '"' : '" OR '}`)
      .join('')

    const paginate = (totalCurrentHits: number) => (totalCurrentHits >= hitsPerPage) && // don't paginate if hitsPerPage is already higher than the total
      setHitsPerPage(h => h + initialHitsPerPage)

    const typeFilter = selectedType && `type:${selectedType}`

    const filters = filterString([
      ...aggregatedGlobalFilters,
      typeFilter,
      selectedTopicsFilter,
    ])

    useEffect(() => {
      if (!router?.query?.topics || initialized.current) return
      setSelectedTopics(initialTopics)
      initialized.current = true
    }, [initialTopics, router?.query?.topics])

    return (
      <InstantSearch
        indexName={indexName}
        searchClient={client}
        resultsState={resultsState(initialHits)}
      >
        <Configure
          hitsPerPage={hitsPerPage}
          filters={filters}
        />
        <searchConfigContext.Provider value={{
          paginate,
          selectedTopics,
          setSelectedTopics,
          selectedType,
          setSelectedType,
        }}>
          {children}
        </searchConfigContext.Provider>
      </InstantSearch>
    );
  }

export {
  searchConfigContext,
  useSearchConfigContext,
}

export default SearchableFeedProvider
